import * as THREE from '../extras/three'
import { Entity } from './Entity'
import { createNode } from '../extras/createNode'
import { LerpQuaternion } from '../extras/LerpQuaternion'
import { LerpVector3 } from '../extras/LerpVector3'
import { hasRank, Ranks } from '../extras/ranks'
import { BufferedLerpVector3 } from '../extras/BufferedLerpVector3'
import { BufferedLerpQuaternion } from '../extras/BufferedLerpQuaternion'
import { Layers } from '../extras/Layers'
import { Emotes, KickTiming, getAttackSwingEndTime, getAttackSwingPhaseDuration } from '../extras/playerEmotes'
import { spawnBloodEffect as spawnBloodHitEffect } from '../extras/bloodEffects'
import { initFootsteps, updateFootsteps, LocomotionModes } from '../extras/playerFootsteps'
import { ALLOW_PLAYER_FLY } from '../extras/matchConfig'
import { isSpectatorSessionAvatar } from '../extras/playerAvatars'
import { JuiceEvents, applyHitstop, flashAvatar } from '../extras/combatJuice'
import { playAttackBackswingGrunt, playAttackSwingGrunt } from '../extras/attackGruntAudio'

let capsuleGeometry
{
  const radius = 0.3
  const inner = 1.2
  const height = radius + inner + radius
  capsuleGeometry = new THREE.CapsuleGeometry(radius, inner) // matches PlayerLocal capsule size
  capsuleGeometry.translate(0, height / 2, 0)
}

const DEATH_EMOTES = [Emotes.DEATH_FALL, Emotes.GETUP]

const COMBAT_EMOTES = [
  Emotes.ATTACK_LEFT,
  Emotes.ATTACK_RIGHT,
  Emotes.ATTACK_HIGH,
  Emotes.ATTACK_LOW,
  Emotes.BLOCK,
  Emotes.BLOCK_LEFT,
  Emotes.BLOCK_RIGHT,
  Emotes.BLOCK_HIGH,
  Emotes.BLOCK_LOW,
  Emotes.KICK,
]

function isDeathEffect(effect) {
  return effect?.emote && DEATH_EMOTES.includes(effect.emote)
}

function sanitizeLocomotionMode(mode, axis) {
  if (ALLOW_PLAYER_FLY || mode !== LocomotionModes.FLY) return mode
  return axis.length() > 0.01 ? LocomotionModes.WALK : LocomotionModes.IDLE
}

export class PlayerRemote extends Entity {
  constructor(world, data, local) {
    super(world, data, local)
    this.isPlayer = true
    this.isRemote = true
    this.init()
  }

  async init() {
    // Sword collision tracking
    this.swordColliderActive = false
    this.hitPlayersThisSwing = new Set()
    this.attackWindupTime = 0.5
    this.attackDuration = 1.0
    this.combatAnimElapsed = 0
    this.combatSwingDuration = null
    this.remoteChargingAttack = false
    this.remoteAttackCommitted = false
    this.attackAnimationPaused = false
    this.lastAttackDuration = 0
    this.currentAttackEmote = null
    
    // Block state
    this.isBlocking = false
    this.currentlyBlocking = false
    this.blockFreezeTime = 0.5
    this.blockAnimElapsed = 0
    this.remoteHoldingBlock = false
    this.blockAnimationPaused = false
    
    // Kick state
    this.kickColliderActive = false
    this.kickColliderDelay = KickTiming.colliderDelay
    this.kickColliderDuration = KickTiming.colliderDuration
    this.kickActivateTimeout = null
    this.kickDeactivateTimeout = null
    this.hitPlayersThisKick = new Set()
    this.currentlyKicking = false
    this.blockForwardOffset = 0.5
    
    // Attack and block tags for directional blocking system
    this.currentAttackTag = null // 'high', 'left', 'right', 'low'
    this.currentBlockTag = null // 'high', 'left', 'right', 'low'
    
    // Death state tracking
    this.isDead = false
    this.deathTimeout = null
    
    // Particle system
    this.activeParticles = []

    this.base = createNode('group')
    this.base.position.fromArray(this.data.position)
    this.base.quaternion.fromArray(this.data.quaternion)

    this.body = createNode('rigidbody', { type: 'kinematic' })
    this.body.active = this.data.effect?.anchorId ? false : true
    this.base.add(this.body)
    this.collider = createNode('collider', {
      type: 'geometry',
      convex: true,
      geometry: capsuleGeometry,
      layer: 'player',
    })
    this.body.add(this.collider)

    // this.caps = createNode('mesh', {
    //   type: 'geometry',
    //   geometry: capsuleGeometry,
    //   material: new THREE.MeshStandardMaterial({ color: 'white' }),
    // })
    // this.base.add(this.caps)

    this.aura = createNode('group')
    this.nametag = createNode('nametag', { label: this.data.name, health: this.data.health, active: false })
    this.aura.add(this.nametag)

    this.bubble = createNode('ui', {
      width: 300,
      height: 512,
      pivot: 'bottom-center',
      billboard: 'full',
      scaler: [3, 30],
      justifyContent: 'flex-end',
      alignItems: 'center',
      active: false,
    })
    this.bubbleBox = createNode('uiview', {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderRadius: 10,
      padding: 10,
    })
    this.bubbleText = createNode('uitext', {
      color: 'white',
      fontWeight: 100,
      lineHeight: 1.4,
      fontSize: 16,
    })
    this.bubble.add(this.bubbleBox)
    this.bubbleBox.add(this.bubbleText)
    this.aura.add(this.bubble)

    this.aura.activate({ world: this.world, entity: this })
    this.base.activate({ world: this.world, entity: this })
    if (this.world.audio) {
      this.footstepAudio = initFootsteps(this.base)
    }

    this.applyAvatar()

    this.position = new BufferedLerpVector3(this.base.position, this.world.networkRate * 1.5)
    this.quaternion = new BufferedLerpQuaternion(this.base.quaternion, this.world.networkRate * 1.5)
    this.teleport = 0

    this.mode = 0
    this.axis = new THREE.Vector3()
    this.gaze = new THREE.Vector3()

    // Set up collider visualization listener
    this.showColliders = false
    this.world.on('showColliders', (show) => {
      this.showColliders = show
      console.log('[PlayerRemote] Show colliders state changed to:', show, 'player:', this.data.id)
      
      // Update visibility if meshes already exist
      if (this.swordColliderMesh) this.swordColliderMesh.visible = show
      if (this.capsuleColliderMesh) this.capsuleColliderMesh.visible = show
    })

    this.world.setHot(this, true)
  }

  applyAvatar() {
    const avatarUrl = this.data.sessionAvatar || this.data.avatar || 'asset://avatar.vrm'
    if (this.avatarUrl === avatarUrl) return
    this.world.loader.load('avatar', avatarUrl).then(src => {
      if (this.avatar) this.avatar.deactivate()
      this.avatar = src.toNodes().get('avatar')
      this.base.add(this.avatar)
      this.nametag.position.y = this.avatar.getHeadToHeight() + 0.2
      this.bubble.position.y = this.avatar.getHeadToHeight() + 0.2
      if (!this.bubble.active) {
        this.nametag.active = true
      }
      this.avatarUrl = avatarUrl
      if (this.isSpectator()) {
        this.removeCombatGear()
      } else {
        this.applySword()
      }
    })
  }

  isSpectator() {
    return isSpectatorSessionAvatar(this.data.sessionAvatar)
  }

  removeCombatGear() {
    this.onAttackCanceled()
    this.clearKickColliderTimeouts()
    this.setBlockColliderActive(false)
    this.currentlyBlocking = false
    this.currentlyAttacking = false
    this.currentlyKicking = false

    if (this.sword) {
      this.sword.deactivate()
      this.sword = null
    }
    if (this.swordHandle) {
      this.swordHandle.destroy()
      this.swordHandle = null
    }
    this.swordBody = null
    this.swordShape = null
    this.swordColliderActive = false

    if (this.blockHandle) {
      this.blockHandle.destroy()
      this.blockHandle = null
    }
    this.blockBody = null
    this.blockShape = null

    if (this.kickHandle) {
      this.kickHandle.destroy()
      this.kickHandle = null
    }
    this.kickBody = null
    this.kickShape = null
    this.kickColliderActive = false

    if (this.world.stage?.scene) {
      for (const mesh of [this.swordColliderMesh, this.blockColliderMesh, this.kickColliderMesh]) {
        if (mesh) {
          this.world.stage.scene.remove(mesh)
        }
      }
      this.swordColliderMesh = null
      this.blockColliderMesh = null
      this.kickColliderMesh = null
    }
  }

  applySword() {
    if (this.isSpectator()) {
      this.removeCombatGear()
      return
    }
    // Load and attach sword to right hand
    this.world.loader
      .load('model', 'asset://sword.glb')
      .then(src => {
        if (this.sword) this.sword.deactivate()
        this.sword = src.toNodes()
        this.sword.activate({ world: this.world, entity: this })
        this.initSwordCollider()
        this.initBlockCollider()
        this.initKickCollider()
      })
      .catch(err => {
        console.error('Failed to load sword:', err)
      })
  }

  initSwordCollider() {
    if (!PHYSX) return
    // Create a box collider for the sword blade
    const width = 0.1
    const height = 1.0
    const depth = 0.05
    const geometry = new PHYSX.PxBoxGeometry(width / 2, height / 2, depth / 2)
    
    const material = this.world.physics.physics.createMaterial(0, 0, 0)
    // Create as a trigger shape (no SIMULATION_SHAPE flag for triggers)
    const flags = new PHYSX.PxShapeFlags(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE)
    
    this.swordShape = this.world.physics.physics.createShape(geometry, material, true, flags)
    
    const filterData = new PHYSX.PxFilterData(
      Layers.weapon.group,
      Layers.weapon.mask,
      PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_FOUND | PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_LOST,
      0
    )
    
    this.swordShape.setQueryFilterData(filterData)
    this.swordShape.setSimulationFilterData(filterData)
    
    const transform = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)
    const v1 = new THREE.Vector3()
    const q1 = new THREE.Quaternion()
    v1.copy(this.base.position).toPxTransform(transform)
    q1.set(0, 0, 0, 1).toPxTransform(transform)
    
    this.swordBody = this.world.physics.physics.createRigidDynamic(transform)
    this.swordBody.setRigidBodyFlag(PHYSX.PxRigidBodyFlagEnum.eKINEMATIC, true)
    this.swordBody.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, true)
    this.swordBody.attachShape(this.swordShape)
    
    const self = this
    this.swordHandle = this.world.physics.addActor(this.swordBody, {
      tag: 'sword',
      playerId: this.data.id,
      onTriggerEnter: (otherHandle) => {
        self.onSwordHit(otherHandle)
      },
    })
    
    this.swordShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, false)
    this.swordColliderActive = false
    
    PHYSX.destroy(geometry)
  }

  initBlockCollider() {
    if (!PHYSX) return
    // Create a square collider in front of the player for blocking
    const capsuleRadius = 0.3
    const capsuleHeight = 1.8
    const width = capsuleRadius * 5
    const height = capsuleHeight * 0.9 * 1.2
    const depth = 0.3
    const geometry = new PHYSX.PxBoxGeometry(width / 2, height / 2, depth / 2)
    
    const material = this.world.physics.physics.createMaterial(0, 0, 0)
    // Create as SIMULATION shape so sword triggers can detect it (trigger-to-trigger doesn't work)
    const flags = new PHYSX.PxShapeFlags(PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE | PHYSX.PxShapeFlagEnum.eSCENE_QUERY_SHAPE)
    
    this.blockShape = this.world.physics.physics.createShape(geometry, material, true, flags)
    
    const filterData = new PHYSX.PxFilterData(
      Layers.player.group,
      Layers.weapon.mask,
      PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_FOUND,
      0
    )
    
    this.blockShape.setQueryFilterData(filterData)
    this.blockShape.setSimulationFilterData(filterData)
    
    const transform = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)
    const v1 = new THREE.Vector3()
    const q1 = new THREE.Quaternion()
    v1.copy(this.base.position).toPxTransform(transform)
    q1.set(0, 0, 0, 1).toPxTransform(transform)
    
    this.blockBody = this.world.physics.physics.createRigidDynamic(transform)
    this.blockBody.setRigidBodyFlag(PHYSX.PxRigidBodyFlagEnum.eKINEMATIC, true)
    this.blockBody.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, true)
    this.blockBody.attachShape(this.blockShape)
    
    // No collision callback needed for remote players' block colliders
    this.blockHandle = this.world.physics.addActor(this.blockBody, {
      tag: 'block',
      playerId: this.data.id,
    })
    
    // Store dimensions for visualization
    this.blockWidth = width
    this.blockHeight = height
    this.blockDepth = depth
    
    this.blockShape.setFlag(PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE, false)
    
    PHYSX.destroy(geometry)
  }

  initKickCollider() {
    if (!PHYSX) return
    const width = 0.5
    const height = 0.5
    const depth = 0.75
    const geometry = new PHYSX.PxBoxGeometry(width / 2, height / 2, depth / 2)

    const material = this.world.physics.physics.createMaterial(0, 0, 0)
    const flags = new PHYSX.PxShapeFlags(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE)

    this.kickShape = this.world.physics.physics.createShape(geometry, material, true, flags)

    const filterData = new PHYSX.PxFilterData(
      Layers.weapon.group,
      Layers.weapon.mask,
      PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_FOUND | PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_LOST,
      0
    )

    this.kickShape.setQueryFilterData(filterData)
    this.kickShape.setSimulationFilterData(filterData)

    const transform = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)
    const v1 = new THREE.Vector3()
    const q1 = new THREE.Quaternion()
    v1.copy(this.base.position).toPxTransform(transform)
    q1.set(0, 0, 0, 1).toPxTransform(transform)

    this.kickBody = this.world.physics.physics.createRigidDynamic(transform)
    this.kickBody.setRigidBodyFlag(PHYSX.PxRigidBodyFlagEnum.eKINEMATIC, true)
    this.kickBody.setActorFlag(PHYSX.PxActorFlagEnum.eDISABLE_GRAVITY, true)
    this.kickBody.attachShape(this.kickShape)

    const self = this
    this.kickHandle = this.world.physics.addActor(this.kickBody, {
      tag: 'kick',
      playerId: this.data.id,
      onTriggerEnter: otherHandle => {
        self.onKickHit(otherHandle)
      },
    })

    this.kickWidth = width
    this.kickHeight = height
    this.kickDepth = depth
    this.kickShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, false)

    PHYSX.destroy(geometry)
  }

  onSwordHit(otherHandle) {
    if (!this.swordColliderActive) return
    
    const playerId = otherHandle.playerId
    if (!playerId) return
    if (playerId === this.data.id) return
    if (this.hitPlayersThisSwing.has(playerId)) return
    
    this.hitPlayersThisSwing.add(playerId)
    
    // Check if we hit a BLOCK collider
    if (otherHandle.tag === 'block') {
      console.log('[Sword Remote] Hit BLOCK from player:', playerId)
      
      // Get blocker entity to check their block tag
      const blocker = this.world.entities.get(playerId)
      if (!blocker) return
      
      // Check if the block direction matches the attack direction
      const attackTag = this.currentAttackTag
      const blockTag = blocker.currentBlockTag
      
      let blockedSuccessfully = false
      
      // If no tags, old block behavior (blocks everything)
      if (!blockTag) {
        blockedSuccessfully = true
      } else if (blockTag && attackTag) {
        // Tag-based blocking: check if tags match
        // high blocks high, low blocks low
        // left blocks RIGHT, right blocks LEFT
        if (blockTag === 'high' && attackTag === 'high') blockedSuccessfully = true
        else if (blockTag === 'low' && attackTag === 'low') blockedSuccessfully = true
        else if (blockTag === 'left' && attackTag === 'right') blockedSuccessfully = true
        else if (blockTag === 'right' && attackTag === 'left') blockedSuccessfully = true
      }
      
      if (blockedSuccessfully) {
        console.log('[Sword Remote] Block SUCCESSFUL - Attack:', attackTag, 'blocked by:', blockTag, '- spawning sparks')
        // Spawn spark particles and play block audio at block position (chest area)
        const blockPos = new THREE.Vector3()
        blockPos.copy(this.base.position)
        blockPos.y += 1.8 * 0.6 // Match block collider height
        this.spawnSparkParticles(blockPos)
        this.playBlockAudio(blockPos)
        return
      } else {
        console.log('[Sword Remote] Block FAILED - Attack:', attackTag, 'vs Block:', blockTag, '- spawning blood, attack continues')
        // Block doesn't match - spawn blood and continue to damage
        const blockPos = new THREE.Vector3()
        blockPos.copy(this.base.position)
        blockPos.y += 1.8 * 0.6
        spawnBloodHitEffect(this.world, this.activeParticles, blockPos)
        this.playHitAudio(blockPos)
        return
      }
    }
    
    // Spawn blood particles and play hit audio at hit location (use sword mesh position)
    if (this.sword) {
      const hitPos = new THREE.Vector3()
      this.sword.getWorldPosition(hitPos)
      spawnBloodHitEffect(this.world, this.activeParticles, hitPos)
      this.playHitAudio(hitPos)
    }

    // Hit confirm for spectators/victims watching this attacker: flash the
    // victim and hitstop both fighters on this client too
    const hitTarget = this.world.entities.get(playerId)
    if (hitTarget) {
      flashAvatar(hitTarget)
      applyHitstop(this, JuiceEvents.hitLanded.hitstopMs)
      applyHitstop(hitTarget, JuiceEvents.hitLanded.hitstopMs)
    }
    
    // Log collision for debugging (damage is handled by server via playerHit message)
    console.log('[Sword Remote] Collision detected between', this.data.id, 'and', playerId)
  }

  onKickHit(otherHandle) {
    // Block breaks are sent to the server; the blocker stops locally and syncs via entityModified
  }

  spawnSparkParticles(position) {
    // Create yellow spark particles
    const particleCount = 10
    const geometry = new THREE.BoxGeometry(0.02, 0.02, 0.02)
    
    for (let i = 0; i < particleCount; i++) {
      const material = new THREE.MeshStandardMaterial({
        color: 0xffff00,
        emissive: 0xffff00,
        emissiveIntensity: 4,
        opacity: 1,
        transparent: true,
      })
      
      const particle = new THREE.Mesh(geometry, material)
      particle.position.set(position.x, position.y, position.z)
      this.world.stage.scene.add(particle)
      
      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        Math.random() * 4 + 2,
        (Math.random() - 0.5) * 6
      )
      
      this.addParticle(particle, velocity, 0.5, 4, material)
    }
  }

  addParticle(particle, velocity, lifetime, initialEmissive, material) {
    const particleData = {
      mesh: particle,
      material: material,
      velocity: velocity,
      lifetime: lifetime,
      elapsed: 0,
      initialEmissive: initialEmissive,
      gravity: 9.8,
    }
    this.activeParticles.push(particleData)
  }

  updateParticles(delta) {
    for (let i = this.activeParticles.length - 1; i >= 0; i--) {
      const p = this.activeParticles[i]
      p.elapsed += delta
      
      // Apply velocity with gravity
      p.mesh.position.x += p.velocity.x * delta
      p.mesh.position.y += p.velocity.y * delta - p.gravity * p.elapsed * delta
      p.mesh.position.z += p.velocity.z * delta
      
      // Fade out
      const alpha = Math.max(0, 1 - p.elapsed / p.lifetime)
      p.material.opacity = alpha
      p.material.emissiveIntensity = p.initialEmissive * alpha
      
      // Remove if expired
      if (p.elapsed >= p.lifetime) {
        this.world.stage.scene.remove(p.mesh)
        p.material.dispose()
        this.activeParticles.splice(i, 1)
      }
    }
  }

  playHitAudio(position) {
    if (!this.world.audio) {
      console.log('[Audio] Audio system not available')
      return
    }
    
    console.log('[Audio Remote] Playing hit sound at position:', position)
    const audio = createNode('audio', {
      src: 'asset://audiohit.mp3',
      volume: 0.5,
      loop: false,
      group: 'sfx',
      spatial: true,
      refDistance: 1,
      maxDistance: 20,
      rolloffFactor: 2,
    })
    
    audio.position.copy(position)
    audio.activate({ world: this.world, entity: this })
    audio.play()
    // Audio will automatically stop and clean up when finished (loop: false)
  }

  playBlockAudio(position) {
    if (!this.world.audio) {
      console.log('[Audio] Audio system not available')
      return
    }
    
    console.log('[Audio Remote] Playing block sound at position:', position)
    const audio = createNode('audio', {
      src: 'asset://audioblock.mp3',
      volume: 0.5,
      loop: false,
      group: 'sfx',
      spatial: true,
      refDistance: 1,
      maxDistance: 20,
      rolloffFactor: 2,
    })
    
    audio.position.copy(position)
    audio.activate({ world: this.world, entity: this })
    audio.play()
    // Audio will automatically stop and clean up when finished (loop: false)
  }

  setSwordColliderActive(active) {
    if (!this.swordShape) return
    
    if (active && !this.swordColliderActive) {
      this.swordShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, true)
      this.swordColliderActive = true
      this.hitPlayersThisSwing.clear()
      console.log('[Sword Remote] Collider activated for player:', this.data.id)
    } else if (!active && this.swordColliderActive) {
      this.swordShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, false)
      this.swordColliderActive = false
      console.log('[Sword Remote] Collider deactivated for player:', this.data.id, '- hit', this.hitPlayersThisSwing.size, 'player(s)')
    }
  }

  setBlockColliderActive(active) {
    if (!this.blockShape) return
    
    if (active) {
      console.log('[Block Remote] Activating block collider for player:', this.data.id, '(SIMULATION shape)')
      this.blockShape.setFlag(PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE, true)
      this.isBlocking = true
    } else {
      console.log('[Block Remote] Deactivating block collider for player:', this.data.id)
      this.blockShape.setFlag(PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE, false)
      this.isBlocking = false
    }
  }

  setKickColliderActive(active) {
    if (!this.kickShape) return

    if (active && !this.kickColliderActive) {
      console.log('[Kick Remote] Activating kick collider for player:', this.data.id)
      this.kickShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, true)
      this.kickColliderActive = true
    } else if (!active && this.kickColliderActive) {
      console.log('[Kick Remote] Deactivating kick collider for player:', this.data.id)
      this.kickShape.setFlag(PHYSX.PxShapeFlagEnum.eTRIGGER_SHAPE, false)
      this.kickColliderActive = false
    }
  }

  clearKickColliderTimeouts() {
    if (this.kickActivateTimeout) {
      clearTimeout(this.kickActivateTimeout)
      this.kickActivateTimeout = null
    }
    if (this.kickDeactivateTimeout) {
      clearTimeout(this.kickDeactivateTimeout)
      this.kickDeactivateTimeout = null
    }
    this.setKickColliderActive(false)
    this.currentlyKicking = false
  }

  getAnchorMatrix() {
    if (this.data.effect?.anchorId) {
      return this.world.anchors.get(this.data.effect.anchorId)
    }
  }

  outranks(otherPlayer) {
    const rank = Math.max(this.data.rank, this.world.settings.effectiveRank)
    const otherRank = Math.max(otherPlayer.data.rank, this.world.settings.effectiveRank)
    return rank > otherRank
  }

  isAdmin() {
    const rank = Math.max(this.data.rank, this.world.settings.effectiveRank)
    return hasRank(rank, Ranks.ADMIN)
  }

  isBuilder() {
    const rank = Math.max(this.data.rank, this.world.settings.effectiveRank)
    return hasRank(rank, Ranks.BUILDER)
  }

  isMuted() {
    return this.world.livekit.isMuted(this.data.id)
  }

  update(delta) {
    // Update particles
    this.updateParticles(delta)
    
    const anchor = this.getAnchorMatrix()
    if (!anchor) {
      this.position.update(delta)
      this.quaternion.update(delta)
    }
    // Check for attack emotes from effects first, otherwise use regular emote
    let emote = this.data.effect?.emote || this.data.emote
    // Combat animations are driven exclusively by effects. `data.emote` can
    // hold a stale attack/block emote between network ticks (e.g. right after
    // a cancel) — never let it (re)start a combat animation.
    if (!this.data.effect?.emote && emote && COMBAT_EMOTES.includes(emote)) {
      emote = null
    }
    if (this.isDead && emote && !DEATH_EMOTES.includes(emote)) {
      emote = Emotes.DEATH_FALL
    }
    
    // Set attack/block tags based on current emote for directional blocking
    if (this.isDead) {
      this.currentAttackTag = null
      this.currentBlockTag = null
    } else if (emote === Emotes.ATTACK_HIGH) this.currentAttackTag = 'high'
    else if (emote === Emotes.ATTACK_LEFT) this.currentAttackTag = 'left'
    else if (emote === Emotes.ATTACK_RIGHT) this.currentAttackTag = 'right'
    else if (emote === Emotes.ATTACK_LOW) this.currentAttackTag = 'low'
    else if (emote === Emotes.BLOCK_HIGH) this.currentBlockTag = 'high'
    else if (emote === Emotes.BLOCK_LEFT) this.currentBlockTag = 'left'
    else if (emote === Emotes.BLOCK_RIGHT) this.currentBlockTag = 'right'
    else if (emote === Emotes.BLOCK_LOW) this.currentBlockTag = 'low'
    else if (emote === Emotes.BLOCK) this.currentBlockTag = null // Old block blocks everything
    else {
      // Clear tags if not attacking or blocking
      this.currentAttackTag = null
      this.currentBlockTag = null
    }
    
    // Pass effect duration if available (important for charged attacks)
    // NOTE: Must call .instance.setEmote() directly to pass duration parameter
    // because the Avatar Node wrapper only accepts one parameter
    let duration = this.data.effect?.duration
    if (this.isDead && emote === Emotes.DEATH_FALL) {
      duration = 1.5
    }
    if (this.avatar?.instance) {
      this.avatar.instance.setEmote(emote, duration)
    }
    const locomotionMode = sanitizeLocomotionMode(this.mode, this.axis)
    this.avatar?.instance?.setLocomotion(locomotionMode, this.axis, this.gaze)

    updateFootsteps(this.footstepAudio, {
      mode: locomotionMode,
      isDead: this.isDead,
      isFlying: false,
      hasEffectEmote: !!this.data.effect?.emote,
    })

    if (this.isDead) return

    if (!this.isSpectator()) {
    // Handle sword collider activation for attack animations
    const attackEmotes = [Emotes.ATTACK_LEFT, Emotes.ATTACK_RIGHT, Emotes.ATTACK_HIGH, Emotes.ATTACK_LOW]
    const isAttacking = this.data.effect?.emote && attackEmotes.includes(this.data.effect.emote)
    const attackDuration = this.data.effect?.duration || 0

    // Detect charged attack (duration > 10 means player is holding)
    const isChargingAttack = isAttacking && attackDuration > 10

    if (isAttacking && !this.currentlyAttacking) {
      this.currentlyAttacking = true
      this.remoteChargingAttack = isChargingAttack
      this.remoteAttackCommitted = false
      this.combatAnimElapsed = 0
      this.combatSwingDuration = null
      this.currentAttackEmote = this.data.effect.emote
      this.lastAttackDuration = attackDuration
      playAttackBackswingGrunt(this.world, this)
    } else if (isAttacking && this.currentlyAttacking) {
      if (this.lastAttackDuration > 10 && attackDuration <= 10) {
        this.remoteChargingAttack = false
        this.remoteAttackCommitted = true
        this.combatAnimElapsed = 0
        this.combatSwingDuration =
          attackDuration > 0 ? attackDuration : getAttackSwingPhaseDuration(this.attackDuration)

        if (this.attackAnimationPaused && this.avatar?.instance?.mixer) {
          this.avatar.instance.mixer.timeScale = 1
          this.attackAnimationPaused = false
        }

        this.setSwordColliderActive(true)
        playAttackSwingGrunt(this.world, this)
      }

      this.lastAttackDuration = attackDuration
    } else if (!isAttacking && this.currentlyAttacking) {
      if (this.combatAnimElapsed < this.attackWindupTime) {
        this.setSwordColliderActive(false)
      }

      this.resetRemoteAttackState()
    }

    this.updateRemoteCombatAttackTiming(delta, { isAttacking, attackDuration, isChargingAttack: isChargingAttack || this.remoteChargingAttack })
    
    // Handle block collider activation for block animation
    const blockEmotes = [Emotes.BLOCK, Emotes.BLOCK_HIGH, Emotes.BLOCK_LEFT, Emotes.BLOCK_RIGHT, Emotes.BLOCK_LOW]
    const isBlocking = this.data.effect?.emote && blockEmotes.includes(this.data.effect.emote)
    const blockDuration = this.data.effect?.duration || 1.0

    if (isBlocking && !this.currentlyBlocking) {
      this.currentlyBlocking = true
      this.remoteHoldingBlock = blockDuration > 10
      this.blockAnimElapsed = 0
      this.blockAnimationPaused = false
      if (this.avatar?.instance?.mixer) {
        this.avatar.instance.mixer.timeScale = 1
      }
      console.log('[Block Remote] Activating block collider for emote:', this.data.effect.emote, 'with tag:', this.currentBlockTag)
      this.setBlockColliderActive(true)
      if (blockDuration < 10) {
        if (this.blockTimeout) clearTimeout(this.blockTimeout)
        this.blockTimeout = setTimeout(() => {
          this.resetRemoteBlockState()
        }, blockDuration * 1000)
      }
    } else if (!isBlocking && this.currentlyBlocking) {
      console.log('[Block Remote] Deactivating block collider')
      this.resetRemoteBlockState()
    }

    this.updateRemoteBlockTiming(delta, { isBlocking, blockDuration })

    // Handle kick collider activation for kick animation
    const isKicking = this.data.effect?.emote === Emotes.KICK

    if (isKicking && !this.currentlyKicking) {
      this.currentlyKicking = true
      this.hitPlayersThisKick.clear()
      this.kickActivateTimeout = setTimeout(() => {
        if (this.currentlyKicking) this.setKickColliderActive(true)
        this.kickActivateTimeout = null
      }, this.kickColliderDelay * 1000)
      this.kickDeactivateTimeout = setTimeout(() => {
        this.setKickColliderActive(false)
        this.kickDeactivateTimeout = null
      }, (this.kickColliderDelay + this.kickColliderDuration) * 1000)
    } else if (!isKicking && this.currentlyKicking) {
      this.clearKickColliderTimeouts()
    }
    }
  }

  lateUpdate(delta) {
    // Create collider meshes if needed (deferred until scene is ready)
    if (this.showColliders && this.world.stage && this.world.stage.scene) {
      // Create capsule mesh if it doesn't exist
      if (!this.capsuleColliderMesh) {
        const radius = 0.3
        const inner = 1.2
        const height = radius + inner + radius
        const capsuleGeom = new THREE.CapsuleGeometry(radius, inner, 8, 16)
        capsuleGeom.translate(0, height / 2, 0)
        const capsuleMat = new THREE.MeshBasicMaterial({
          color: 0x0000ff,
          transparent: true,
          opacity: 0.3,
          wireframe: false,
          depthTest: true,
        })
        this.capsuleColliderMesh = new THREE.Mesh(capsuleGeom, capsuleMat)
        this.capsuleColliderMesh.visible = true
        this.world.stage.scene.add(this.capsuleColliderMesh)
        console.log('[PlayerRemote] Created capsule collider mesh (deferred) for player:', this.data.id)
      }
      
      // Create sword mesh if it doesn't exist and we have the sword shape
      if (!this.swordColliderMesh && this.swordShape) {
        const width = 0.1
        const height = 1.0
        const depth = 0.05
        const boxGeom = new THREE.BoxGeometry(width, height, depth)
        const boxMat = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          transparent: true,
          opacity: 0.3,
          wireframe: false,
          depthTest: true,
        })
        this.swordColliderMesh = new THREE.Mesh(boxGeom, boxMat)
        this.swordColliderMesh.visible = true
        this.world.stage.scene.add(this.swordColliderMesh)
        console.log('[PlayerRemote] Created sword collider mesh (deferred) for player:', this.data.id)
      }
      
      // Create block mesh if it doesn't exist and we have the block shape
      if (!this.blockColliderMesh && this.blockShape) {
        const blockGeom = new THREE.BoxGeometry(this.blockWidth, this.blockHeight, this.blockDepth)
        const blockMat = new THREE.MeshBasicMaterial({
          color: 0xffff00, // Yellow (changes to green when blocking)
          transparent: true,
          opacity: 0.2,
          wireframe: false,
          depthTest: true,
        })
        this.blockColliderMesh = new THREE.Mesh(blockGeom, blockMat)
        this.blockColliderMesh.visible = true
        this.world.stage.scene.add(this.blockColliderMesh)
        console.log('[PlayerRemote] Created block collider mesh (deferred) for player:', this.data.id)
      }

      // Create kick mesh if it doesn't exist and we have the kick shape
      if (!this.kickColliderMesh && this.kickShape) {
        const kickGeom = new THREE.BoxGeometry(this.kickWidth, this.kickHeight, this.kickDepth)
        const kickMat = new THREE.MeshBasicMaterial({
          color: 0xff8800,
          transparent: true,
          opacity: 0.2,
          wireframe: false,
          depthTest: true,
        })
        this.kickColliderMesh = new THREE.Mesh(kickGeom, kickMat)
        this.kickColliderMesh.visible = true
        this.world.stage.scene.add(this.kickColliderMesh)
        console.log('[PlayerRemote] Created kick collider mesh (deferred) for player:', this.data.id)
      }
    }

    const anchor = this.getAnchorMatrix()
    if (anchor) {
      this.position.snap()
      this.quaternion.snap()
      this.base.position.setFromMatrixPosition(anchor)
      this.base.quaternion.setFromRotationMatrix(anchor)
      this.base.clean()
    }
    if (this.avatar) {
      const matrix = this.avatar.getBoneTransform('head')
      if (matrix) {
        this.aura.position.setFromMatrixPosition(matrix)
      }
    }
    if (this.avatar && this.sword && !this.isSpectator()) {
      const matrix = this.avatar.getBoneTransform('rightHand')
      if (matrix) {
        const v5 = new THREE.Vector3()
        const v6 = new THREE.Vector3()
        const q4 = new THREE.Quaternion()
        
        // Get base transform from hand bone
        this.sword.position.setFromMatrixPosition(matrix)
        this.sword.quaternion.setFromRotationMatrix(matrix)
        
        // Scale the sword down to a reasonable size
        this.sword.scale.set(0.7, 0.7, 0.7)
        
        // Apply rotation offset to orient sword properly in hand
        q4.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2)
        this.sword.quaternion.multiply(q4)
        
        // Apply position offset relative to hand orientation
        v5.set(0.06, -0.5, 0.05)
        v5.applyQuaternion(this.sword.quaternion)
        this.sword.position.add(v5)

        // Update sword collider position to match sword mesh
        if (this.swordBody) {
          const pose = this.swordBody.getGlobalPose()
          v6.set(0, -0.1, 0) // Move collider to middle of blade
          v6.applyQuaternion(this.sword.quaternion)
          v6.add(this.sword.position)
          v6.toPxTransform(pose)
          this.sword.quaternion.toPxTransform(pose)
          this.swordBody.setGlobalPose(pose)

          // Update sword collider visualization mesh
          if (this.swordColliderMesh) {
            this.swordColliderMesh.position.copy(v6)
            this.swordColliderMesh.quaternion.copy(this.sword.quaternion)
          }
        }
      }
    }

    // Update block collider position (always positioned in front of player)
    if (this.blockBody) {
      const v6 = new THREE.Vector3()
      const pose = this.blockBody.getGlobalPose()
      // Position block collider in front of player at chest height
      const forwardOffset = 0.5
      const heightOffset = 1.8 * 0.6 // Upper mid-body
      
      v6.set(0, 0, -forwardOffset) // Negative Z is forward in Three.js
      v6.applyQuaternion(this.base.quaternion)
      v6.add(this.base.position)
      v6.y += heightOffset
      
      v6.toPxTransform(pose)
      this.base.quaternion.toPxTransform(pose)
      this.blockBody.setGlobalPose(pose)

      // Update block collider visualization mesh
      if (this.blockColliderMesh) {
        this.blockColliderMesh.position.copy(v6)
        this.blockColliderMesh.quaternion.copy(this.base.quaternion)
        
        // Change color based on blocking state
        const material = this.blockColliderMesh.material
        if (this.isBlocking) {
          material.color.setHex(0x00ff00) // Green when blocking
          material.opacity = 0.5
        } else {
          material.color.setHex(0xffff00) // Yellow when not blocking
          material.opacity = 0.2
        }
      }
    }

    // Update kick collider position (extends forward from block collider center)
    if (this.kickBody) {
      const v3 = new THREE.Vector3()
      const v6 = new THREE.Vector3()
      const pose = this.kickBody.getGlobalPose()
      const blockForwardOffset = this.blockForwardOffset ?? 0.5
      const heightOffset = 1.8 * 0.6

      v6.set(0, 0, -blockForwardOffset)
      v6.applyQuaternion(this.base.quaternion)
      v6.add(this.base.position)
      v6.y += heightOffset

      v3.set(0, 0, -this.kickDepth / 2)
      v3.applyQuaternion(this.base.quaternion)
      v6.add(v3)

      v6.toPxTransform(pose)
      this.base.quaternion.toPxTransform(pose)
      this.kickBody.setGlobalPose(pose)

      if (this.kickColliderMesh) {
        this.kickColliderMesh.position.copy(v6)
        this.kickColliderMesh.quaternion.copy(this.base.quaternion)

        const material = this.kickColliderMesh.material
        if (this.kickColliderActive) {
          material.color.setHex(0xff4400)
          material.opacity = 0.5
        } else {
          material.color.setHex(0xff8800)
          material.opacity = 0.2
        }
      }
    }

    // Update capsule collider visualization mesh
    if (this.capsuleColliderMesh) {
      this.capsuleColliderMesh.position.copy(this.base.position)
      this.capsuleColliderMesh.quaternion.copy(this.base.quaternion)
    }
  }

  isRemoteAnimationPaused() {
    return this.avatar?.instance?.mixer?.timeScale === 0
  }

  resetRemoteAttackState() {
    this.currentlyAttacking = false
    this.remoteChargingAttack = false
    this.remoteAttackCommitted = false
    this.combatAnimElapsed = 0
    this.combatSwingDuration = null
    this.currentAttackEmote = null
    this.setSwordColliderActive(false)
    if (this.attackAnimationPaused && this.avatar?.instance?.mixer) {
      this.avatar.instance.mixer.timeScale = 1
      this.attackAnimationPaused = false
    }
  }

  updateRemoteCombatAttackTiming(delta, { isAttacking, attackDuration, isChargingAttack }) {
    if (!isAttacking || !this.currentlyAttacking) {
      if (!isAttacking) {
        this.combatAnimElapsed = 0
        this.combatSwingDuration = null
      }
      return
    }

    if (!this.isRemoteAnimationPaused()) {
      this.combatAnimElapsed += delta
    }

    if (isChargingAttack && !this.attackAnimationPaused && this.combatAnimElapsed >= this.attackWindupTime) {
      if (this.avatar?.instance?.mixer) {
        this.avatar.instance.mixer.timeScale = 0
        this.attackAnimationPaused = true
      }
      return
    }

    if (!isChargingAttack && !this.remoteAttackCommitted && this.combatAnimElapsed >= this.attackWindupTime) {
      this.remoteAttackCommitted = true
      this.setSwordColliderActive(true)
      playAttackSwingGrunt(this.world, this)
    }

    const swingLimit =
      this.combatSwingDuration ??
      (isChargingAttack ? null : getAttackSwingEndTime(this.attackWindupTime, this.attackDuration))
    if (this.remoteAttackCommitted && swingLimit != null && this.combatAnimElapsed >= swingLimit) {
      this.setSwordColliderActive(false)
    }
  }

  resetRemoteBlockState() {
    this.currentlyBlocking = false
    this.remoteHoldingBlock = false
    this.blockAnimElapsed = 0
    if (this.blockAnimationPaused && this.avatar?.instance?.mixer) {
      this.avatar.instance.mixer.timeScale = 1
      this.blockAnimationPaused = false
    }
    if (this.blockTimeout) {
      clearTimeout(this.blockTimeout)
      this.blockTimeout = null
    }
    this.setBlockColliderActive(false)
  }

  updateRemoteBlockTiming(delta, { isBlocking, blockDuration }) {
    if (!isBlocking) {
      if (this.blockAnimationPaused && this.avatar?.instance?.mixer) {
        this.avatar.instance.mixer.timeScale = 1
        this.blockAnimationPaused = false
      }
      this.remoteHoldingBlock = false
      this.blockAnimElapsed = 0
      return
    }

    if (!this.remoteHoldingBlock || this.blockAnimationPaused) return

    this.blockAnimElapsed += delta
    if (this.blockAnimElapsed >= this.blockFreezeTime) {
      this.blockAnimationPaused = true
      if (this.avatar?.instance?.mixer) {
        this.avatar.instance.mixer.timeScale = 0
      }
    }
  }

  onAttackCanceled() {
    console.log('[Attack] Remote player canceled attack early')

    // Resume animation if it was paused
    if (this.attackAnimationPaused && this.avatar?.instance?.mixer) {
      this.avatar.instance.mixer.timeScale = 1
      this.attackAnimationPaused = false
    }

    // Force switch back to locomotion by clearing the emote — immediate so
    // the swing stops now instead of playing out visually
    if (this.avatar?.instance) {
      this.avatar.instance.setEmote(null, undefined, { immediate: true })
    }

    // Clear the effect data
    this.data.effect = null

    this.resetRemoteAttackState()
  }

  setPlayerColliderActive(active) {
    if (this.body) {
      this.body.active = active && !this.data.effect?.anchorId
    }
  }

  setEffect(effect, onEnd) {
    if (this.data.effect) {
      this.data.effect = null
      this.onEffectEnd?.()
      this.onEffectEnd = null
    }
    this.data.effect = effect
    this.onEffectEnd = onEnd
    this.setPlayerColliderActive(!this.isDead)
  }

  setSpeaking(speaking) {
    if (this.speaking === speaking) return
    if (speaking && this.isMuted()) return
    this.speaking = speaking
    const name = this.data.name
    this.nametag.label = speaking ? `» ${name} «` : name
  }

  modify(data) {
    let avatarChanged
    if (data.hasOwnProperty('t')) {
      this.teleport++
    }
    if (data.hasOwnProperty('p')) {
      this.data.position = data.p
      this.position.push(data.p, this.teleport)
    }
    if (data.hasOwnProperty('q')) {
      this.data.quaternion = data.q
      this.quaternion.push(data.q, this.teleport)
    }
    if (data.hasOwnProperty('m')) {
      this.data.mode = data.m
      this.mode = data.m
    }
    if (data.hasOwnProperty('a')) {
      this.data.axis = data.a
      this.axis.fromArray(data.a)
    }
    if (data.hasOwnProperty('m') || data.hasOwnProperty('a')) {
      this.mode = sanitizeLocomotionMode(this.mode, this.axis)
      this.data.mode = this.mode
    }
    if (data.hasOwnProperty('g')) {
      this.data.gaze = data.g
      this.gaze.fromArray(data.g)
    }
    if (data.hasOwnProperty('e')) {
      this.data.emote = data.e
    }
    if (data.hasOwnProperty('name')) {
      this.data.name = data.name
      this.nametag.label = data.name
      this.world.emit('name', { playerId: this.data.id, name: this.data.name })
    }
    if (data.hasOwnProperty('health')) {
      const previousHealth = this.data.health
      this.data.health = data.health
      this.nametag.health = data.health
      console.log('[Health] Remote player', this.data.id, 'health updated to:', data.health)
      this.world.events.emit('health', { playerId: this.data.id, health: data.health })
      
      // Handle death and respawn
      if (data.health <= 0 && (previousHealth === undefined || previousHealth > 0)) {
        this.onDeath()
      } else if (data.health > 0 && this.isDead) {
        // Healing while dead = respawn
        if (this.deathTimeout) {
          clearTimeout(this.deathTimeout)
          this.deathTimeout = null
        }
        this.onRespawn()
      }
    }
    if (data.hasOwnProperty('ef')) {
      if (!(this.isDead && data.ef && !isDeathEffect(data.ef))) {
        this.setEffect(data.ef)
      }
    }
    if (data.hasOwnProperty('avatar')) {
      this.data.avatar = data.avatar
      avatarChanged = true
    }
    if (data.hasOwnProperty('sessionAvatar')) {
      this.data.sessionAvatar = data.sessionAvatar
      avatarChanged = true
    }
    if (data.hasOwnProperty('rank')) {
      this.data.rank = data.rank
      this.world.emit('rank', { playerId: this.data.id, rank: this.data.rank })
    }
    if (avatarChanged) {
      this.applyAvatar()
    }
  }

  chat(msg) {
    this.nametag.active = false
    this.bubbleText.value = msg
    this.bubble.active = true
    clearTimeout(this.chatTimer)
    this.chatTimer = setTimeout(() => {
      this.bubble.active = false
      this.nametag.active = true
    }, 5000)
  }

  onDeath() {
    console.log('[Death] Remote player', this.data.id, 'died - starting death sequence')
    this.isDead = true
    this.world.emit('arenaDeath', { playerId: this.data.id })
    
    // Cancel any active attacks/blocks/kicks
    this.resetRemoteAttackState()
    this.resetRemoteBlockState()
    this.currentAttackTag = null
    this.currentBlockTag = null
    this.clearKickColliderTimeouts()
    this.setPlayerColliderActive(false)
    
    // Tell avatar to use dead animation as locomotion
    if (this.avatar && this.avatar.instance && this.avatar.instance.setDeathState) {
      this.avatar.instance.setDeathState(true)
    }
    
    // Play fall immediately so stale combat effects cannot freeze the pose
    this.setEffect({
      emote: Emotes.DEATH_FALL,
      duration: 1.5,
      cancellable: false,
    })
    
    this.deathTimeout = setTimeout(() => {
      console.log('[Respawn] Remote player', this.data.id, 'respawn timing complete')
    }, 7000)
  }
  
  onRespawn() {
    console.log('[Respawn] Remote player', this.data.id, 'respawning')
    this.isDead = false
    this.setEffect(null)
    this.setPlayerColliderActive(true)

    // Restore normal locomotion
    if (this.avatar?.instance?.setDeathState) {
      this.avatar.instance.setDeathState(false)
    }
    if (!this.avatar) {
      this.avatarUrl = null
      this.applyAvatar()
    }
  }

  destroy(local) {
    if (this.destroyed) return
    this.destroyed = true

    clearTimeout(this.chatTimer)
    clearTimeout(this.deathTimeout)
    this.base.deactivate()
    this.avatar = null
    if (this.sword) this.sword.deactivate()
    this.sword = null

    // Clean up sword collider
    if (this.swordHandle) {
      this.swordHandle.destroy()
      this.swordHandle = null
    }
    if (this.swordBody) {
      this.swordBody = null
    }
    if (this.swordShape) {
      this.swordShape = null
    }

    // Clean up visualization meshes (client only)
    if (this.world.stage && this.world.stage.scene) {
      if (this.swordColliderMesh) {
        this.world.stage.scene.remove(this.swordColliderMesh)
        this.swordColliderMesh = null
      }
      if (this.capsuleColliderMesh) {
        this.world.stage.scene.remove(this.capsuleColliderMesh)
        this.capsuleColliderMesh = null
      }
    }

    this.world.setHot(this, false)
    this.world.events.emit('leave', { playerId: this.data.id })
    this.aura.deactivate()
    this.aura = null

    this.world.entities.remove(this.data.id)
    // if removed locally we need to broadcast to server/clients
    if (local) {
      this.world.network.send('entityRemoved', this.data.id)
    }
  }
}
